/**
 * usePageTracking
 *
 * Mede o tempo que o usuário permanece numa tela e envia ao backend
 * quando sai dela (unmount ou visibilitychange para "hidden").
 *
 * Uso:
 *   usePageTracking('/events', 'Eventos');
 *
 * Requisitos:
 *   - Deve ser chamado no topo de cada page-level component.
 *   - Ignora views < 2 s (o backend também filtra, mas evitamos a request).
 *   - Usa navigator.sendBeacon quando disponível (sobrevive ao unload).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { analyticsApi } from '../lib/api';
import { BASE } from '../lib/api';

// Gera/recupera um token de sessão estável por aba do browser
function getSessionToken(): string {
  const KEY = 'emss_session_token';
  let tok = sessionStorage.getItem(KEY);
  if (!tok) {
    tok = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(KEY, tok);
  }
  return tok;
}

export function usePageTracking(page: string, pageLabel?: string) {
  const { emssUser } = useAuth();
  const startRef = useRef<number>(Date.now());
  const sentRef  = useRef<boolean>(false);

  // Redefine o timer quando a rota muda
  useEffect(() => {
    startRef.current = Date.now();
    sentRef.current  = false;

    const sendView = (reason: 'unmount' | 'hidden') => {
      if (sentRef.current) return;
      const durationSec = Math.floor((Date.now() - startRef.current) / 1000);
      if (durationSec < 2) return; // ignora bounces

      sentRef.current = true;

      const payload = {
        page,
        page_label: pageLabel ?? page,
        duration_sec: durationSec,
        session_token: getSessionToken(),
      };

      // Prefere sendBeacon (funciona no unload sem bloquear)
      const url = `${BASE}/analytics/page-view`;
      if (typeof navigator.sendBeacon === 'function' && reason === 'hidden') {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        // Fallback silencioso — não bloqueia UX
        analyticsApi.trackPageView(payload).catch(() => {});
      }
    };

    // Dispara quando o usuário minimiza aba / troca de aba
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') sendView('hidden');
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      sendView('unmount');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
}
